import { useSyncExternalStore } from 'react'

// ---------------------------------------------------------------------------
// Runtime per-brand SMS-marketing switch. Replaces the build-time
// NEXT_PUBLIC_SMS_* env gates: the value now comes from the brand's
// `smsMarketingEnabled` flag (Rails admin -> Brand model -> GraphQL getBrand),
// fetched once by <SmsMarketingConfigBootstrap /> in AppProviders and published
// here as a module singleton — mirroring the Rudderstack SDK pattern in
// useRudderstackAnalytics.ts.
//
// One flag gates all four marketing surfaces: footer modal, SMS-only modal,
// order-confirmation card, and the My Account combined card. Transactional SMS
// (sms_tracking) is unrelated and unaffected.
//
// Two consumers: React components use useSmsMarketingEnabled() (reactive, so a
// surface stays hidden until the fetch resolves — no flash), and non-React code
// (smsOptInModalQueue.ts) uses isSmsMarketingEnabled() synchronously at event
// time, after the bootstrap has resolved.
// ---------------------------------------------------------------------------

export interface SmsMarketingConfig {
  /** Whether the current brand's SMS marketing surfaces are enabled. */
  enabled: boolean
  /** Whether the brand-config fetch has resolved yet (false = still loading). */
  loaded: boolean
}

let smsMarketingEnabled = false
let loaded = false
// useSyncExternalStore requires a referentially-stable snapshot — cache it and
// only replace it when the value actually changes.
let snapshot: SmsMarketingConfig = { enabled: false, loaded: false }
const subscribers = new Set<() => void>()

const getSnapshot = (): SmsMarketingConfig => snapshot

export const subscribeToSmsMarketingConfig = (onChange: () => void): (() => void) => {
  subscribers.add(onChange)
  return () => {
    subscribers.delete(onChange)
  }
}

/** Synchronous getter for non-React callers (e.g. smsOptInModalQueue.ts). */
export const isSmsMarketingEnabled = (): boolean => smsMarketingEnabled

/** Whether the brand-config fetch has resolved. */
export const isSmsMarketingConfigLoaded = (): boolean => loaded

/**
 * Publish the fetched brand flag. Called once by the bootstrap on load; a
 * no-op when the value is unchanged so subscribers aren't churned.
 */
export const setSmsMarketingConfig = (enabled: boolean): void => {
  if (loaded && enabled === smsMarketingEnabled) return
  smsMarketingEnabled = enabled
  loaded = true
  snapshot = { enabled, loaded: true }
  subscribers.forEach((notify) => notify())
}

/**
 * Reactive hook for React components. Returns { enabled, loaded }; re-renders
 * when the brand-config fetch resolves. Keep surfaces hidden until `loaded` (or
 * simply gate on `enabled`, which is false until then) to avoid a flash.
 */
export const useSmsMarketingEnabled = (): SmsMarketingConfig =>
  useSyncExternalStore(subscribeToSmsMarketingConfig, getSnapshot, getSnapshot)
